import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaCircle, FaArrowLeft } from 'react-icons/fa';
import { fetchPositionsForWA, Position, PreviewResponse } from '../services/ContractWizardService';
import { getTemplate, Template } from '../../templates/services/TemplatesService';
import { fetchAttachments, AttachmentTemplate, AttachmentSelection } from '../services/AttachmentService';
import { useCompensationUnlock } from '../../employees/hooks/useCompensationUnlock';
import { EMPLOYMENT_TYPE_OPTIONS } from '../../employees/utils/constants';
import { useUserRole } from '../../../hooks/useUserRole';
import { toast } from 'react-toastify';
import { PreviewPane } from './PreviewPane';
import { AnnexQuickCreateModal } from './AnnexQuickCreateModal';
import { derivePresenceFromSchema, ConditionalFieldPresence } from '../utils/conditionals';
import { ContractStepPosition } from './ContractStepPosition';
import { ContractStepDetails } from './ContractStepDetails';
import {
  ANY_POSITION, weeklyToMonthly, monthlyToWeekly, findTemplate,
  filterRequiredFields, filterOptionalFields, isFormComplete,
  ContractWizardModalProps,
} from './useContractWizard';
import { createHandlers } from './useContractHandlers';
import css from './ContractWizardModal.module.css';
import './ContractWizardModal.css';

export function ContractWizardModal({
  open, onClose, employeeId, workAreas, currentEmploymentType, currentWorkAreaId,
  currentPositionKey, currentPositionTitle, currentStartDate, onSuccess, asDrawer = false,
}: ContractWizardModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedWA, setSelectedWA] = useState<number | null>(currentWorkAreaId || null);
  const [selectedEmploymentType, setSelectedEmploymentType] = useState(currentEmploymentType || '');
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedPositionKey, setSelectedPositionKey] = useState(currentPositionKey || '');
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | Blob | undefined>(undefined);
  const [loadingPdfPreview, setLoadingPdfPreview] = useState(false);
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(0);
  const [patchProfile, setPatchProfile] = useState(true);
  const [startDate, setStartDate] = useState(currentStartDate || '');
  const [contractEndDate, setContractEndDate] = useState('');
  const [probationMonths, setProbationMonths] = useState<number | null>(null);
  const [extraLeaveDays, setExtraLeaveDays] = useState<number | null>(null);
  const [compensationType, setCompensationType] = useState<'hourly' | 'salary'>('salary');
  const [annexModalOpen, setAnnexModalOpen] = useState(false);
  const [hourlyRate, setHourlyRate] = useState<number | null>(null);
  const [salaryMonthlyCents, setSalaryMonthlyCents] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [collapsedBlocks, setCollapsedBlocks] = useState<Record<string, boolean>>({});
  const [seeding, setSeeding] = useState(false);
  const [blockValues, setBlockValues] = useState<Record<string, string>>({});
  const [initialBlockValues, setInitialBlockValues] = useState<Record<string, string>>({});
  const [minijobWarning, setMinijobWarning] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [schemaKeys, setSchemaKeys] = useState<string[] | null>(null);
  const [presence, setPresence] = useState<ConditionalFieldPresence>({ hasFixedTerm: false, hasOtherEmployment: false, includeAddressFields: false });
  const [prefillsApplied, setPrefillsApplied] = useState(false);
  const [originalFormValues, setOriginalFormValues] = useState<Record<string, any>>({});
  const [visibleOptionalFieldKeys, setVisibleOptionalFieldKeys] = useState<Set<string>>(new Set());
  const [attachments, setAttachments] = useState<AttachmentTemplate[]>([]);
  const [attachmentSelections, setAttachmentSelections] = useState<AttachmentSelection[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentValidationErrors, setAttachmentValidationErrors] = useState<Record<string, string>>({});
  const [mergeWarnings, setMergeWarnings] = useState<string[]>([]);
  const [makeCoverPage, setMakeCoverPage] = useState(true);
  const [makeCombinedPdf, setMakeCombinedPdf] = useState(true);

  const navigate = useNavigate();
  const { isAdmin, isManager } = useUserRole();
  const { ensureUnlocked, showPinDialog, pinError, handlePinSubmit, cancelUnlock } = useCompensationUnlock();
  const steps = ['Work Area & Position', 'Contract Parameters'];

  const handlers = createHandlers({
    employeeId, selectedWA, selectedEmploymentType, selectedPositionKey, startDate,
    compensationType, formValues, positions, presence, allTemplates, selectedTemplateId,
    attachments, attachmentSelections, blockValues, initialBlockValues, patchProfile,
    contractEndDate, probationMonths, extraLeaveDays, makeCoverPage, makeCombinedPdf, preview,
    setAllTemplates, setLoadingTemplates, setLoadingPreview, setLoadingPdfPreview, setPreview,
    setPreviewSrc, setSelectedTemplateId, setSelectedSuggestionIdx, setError, setFormValues,
    setBlockValues, setInitialBlockValues, setProbationMonths, setExtraLeaveDays,
    setCompensationType, setHourlyRate, setValidationErrors, setAttachmentValidationErrors,
    setLoading, setMergeWarnings, setSeeding, setActiveStep, onSuccess, onClose, ensureUnlocked, navigate,
  });

  /* --- Effects --- */

  useEffect(() => {
    if (!selectedEmploymentType) return;
    const t = selectedEmploymentType.trim().toUpperCase();
    setCompensationType(t === 'VOLLZEIT' ? 'salary' : 'hourly');
  }, [selectedEmploymentType]);

  useEffect(() => {
    if (currentStartDate && !startDate) {
      let d = currentStartDate;
      if (d.includes('.')) { const p = d.split('.'); if (p.length === 3) d = `${p[2]}-${p[1]}-${p[0]}`; }
      setStartDate(d);
    }
  }, [currentStartDate, startDate]);

  useEffect(() => {
    if (!open) return;
    templateFetchAttemptedRef.current = null;
    setActiveStep(0); setError(''); setSelectedWA(currentWorkAreaId || null);
    setSelectedEmploymentType(currentEmploymentType || ''); setSelectedPositionKey('');
    setPositions([]); setPreview(null); setSelectedTemplateId(''); setSelectedSuggestionIdx(0);
    setPatchProfile(true); setStartDate(''); setContractEndDate(''); setProbationMonths(null);
    setExtraLeaveDays(null);
    const t = (currentEmploymentType || '').trim().toUpperCase();
    setCompensationType(t === 'VOLLZEIT' ? 'salary' : 'hourly');
    setHourlyRate(null); setSalaryMonthlyCents(null); setFormValues({}); setValidationErrors({});
    setBlockValues({}); setInitialBlockValues({}); setMinijobWarning(''); setPdfUrl('');
    setLoadingPdfPreview(false);
  }, [open, currentWorkAreaId, currentEmploymentType]);

  useEffect(() => {
    const t = (selectedEmploymentType || '').toUpperCase();
    if (t === 'MINIJOB' && compensationType === 'hourly') {
      const hr = formValues['hourly_rate'];
      if (hr && hr > 0) {
        const max = 556.0 / hr;
        setFormValues((prev) => ({ ...prev, contract_hours_per_week: Math.floor(monthlyToWeekly(max) * 100) / 100, monthly_hours: Math.floor(max * 100) / 100 }));
        setMinijobWarning('');
      } else { setMinijobWarning(''); }
    } else { setMinijobWarning(''); }
  }, [selectedEmploymentType, compensationType, formValues['hourly_rate']]);

  useEffect(() => {
    const t = (selectedEmploymentType || '').toUpperCase();
    if (compensationType === 'hourly' && t !== 'MINIJOB') {
      const w = formValues['contract_hours_per_week'];
      if (w && w > 0) setFormValues((prev) => ({ ...prev, monthly_hours: weeklyToMonthly(Number(w)) }));
    }
  }, [compensationType, formValues['contract_hours_per_week'], selectedEmploymentType, activeStep]);

  useEffect(() => {
    const t = (selectedEmploymentType || '').toUpperCase();
    if (t === 'MINIJOB' && compensationType === 'hourly') {
      const hr = Number(formValues['hourly_rate']), wh = Number(formValues['contract_hours_per_week']);
      if (hr > 0 && wh > 0) {
        const mh = weeklyToMonthly(wh), total = hr * mh;
        if (total > 556.5) setMinijobWarning(`⚠️ Warnung: ${hr.toFixed(2)} €/h × ${wh} h/Woche (≈ ${mh.toFixed(2)} h/Monat) = ${total.toFixed(2)} €/Monat übersteigt die Minijob-Grenze von 556.00 €`);
        else setMinijobWarning('');
      }
    }
  }, [selectedEmploymentType, compensationType, formValues['hourly_rate'], formValues['contract_hours_per_week']]);

  useEffect(() => {
    if (compensationType === 'salary') {
      const mh = formValues['monthly_hours'];
      if (mh && mh > 0) setFormValues((prev) => ({ ...prev, contract_hours_per_week: monthlyToWeekly(Number(mh)) }));
    }
  }, [compensationType, formValues['monthly_hours']]);

  useEffect(() => {
    if (selectedWA) {
      setLoadingPositions(true);
      fetchPositionsForWA(selectedWA).then((data) => setPositions([ANY_POSITION as any, ...data])).catch(() => setPositions([ANY_POSITION as any])).finally(() => setLoadingPositions(false));
    } else { setPositions([]); setSelectedPositionKey(''); }
  }, [selectedWA]);

  useEffect(() => {
    if (selectedWA && activeStep === 1) {
      setLoadingAttachments(true);
      fetchAttachments(selectedWA, selectedPositionKey || null).then((r) => {
        const list = r.attachments || []; setAttachments(list);
        setAttachmentSelections(list.filter((a) => a.default_selected === true).map((a, i) => ({ template_id: a.template_id, order_index: a.order_index || i + 1, variables: {} })));
      }).catch(() => setAttachments([])).finally(() => setLoadingAttachments(false));
    }
  }, [selectedWA, selectedPositionKey, activeStep]);

  const templateFetchAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTemplateId) return;
    let alive = true;
    (async () => {
      try {
        let t = findTemplate(allTemplates, selectedTemplateId);
        if (!t && templateFetchAttemptedRef.current !== selectedTemplateId) {
          templateFetchAttemptedRef.current = selectedTemplateId;
          const fresh = await handlers.loadAllTemplates();
          t = findTemplate(fresh, selectedTemplateId);
        }
        if (!t) {
          if (!alive) return;
          setSchemaKeys(null);
          setPresence({ hasFixedTerm: true, hasOtherEmployment: true, includeAddressFields: false });
          return;
        }
        const tpl = await getTemplate(t.id); if (!alive) return;
        const keys = tpl?.schema?.all_keys ?? [...tpl.schema.required_fields.map((f: any) => f.key), ...tpl.schema.optional_fields.map((f: any) => f.key)];
        setSchemaKeys(keys); setPresence(derivePresenceFromSchema(keys));
      } catch { if (!alive) return; setSchemaKeys(null); setPresence({ hasFixedTerm: true, hasOtherEmployment: true, includeAddressFields: false }); }
    })();
    return () => { alive = false; };
  }, [selectedTemplateId, allTemplates]);

  useEffect(() => { setVisibleOptionalFieldKeys(new Set()); }, [selectedTemplateId]);
  useEffect(() => { setPrefillsApplied(false); }, [selectedPositionKey]);
  useEffect(() => { if (activeStep === 0) setPrefillsApplied(false); }, [activeStep]);

  useEffect(() => {
    if (selectedPositionKey && selectedPositionKey !== '' && positions.length > 0 && activeStep === 1 && !prefillsApplied) {
      if (positions.find((p) => p.position_key === selectedPositionKey)) applyPositionPrefills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPositionKey, positions, activeStep, prefillsApplied]);

  /* --- Derived --- */
  const filteredPositions = useMemo(() => {
    if (!selectedEmploymentType) return positions;
    return positions.filter((p) => !p.allowed_employment_types || p.allowed_employment_types.length === 0 || p.allowed_employment_types.includes(selectedEmploymentType));
  }, [positions, selectedEmploymentType]);

  const selectedEmploymentTypeLabel = useMemo(() => { const o = EMPLOYMENT_TYPE_OPTIONS.find((opt) => opt.value === selectedEmploymentType); return o ? o.label : selectedEmploymentType; }, [selectedEmploymentType]);
  const selectedWAName = useMemo(() => { const w = workAreas.find((w) => w.id === selectedWA); return w ? w.name : ''; }, [selectedWA, workAreas]);
  const selectedPositionLabel = useMemo(() => { if (selectedPositionKey === null) return 'Allgemein (ohne Position)'; const p = positions.find((p) => p.position_key === selectedPositionKey); return p ? p.label : ''; }, [selectedPositionKey, positions]);
  const filteredRequiredFields = useMemo(() => filterRequiredFields(preview?.schema?.required_fields), [preview?.schema?.required_fields]);
  const filteredOptionalFields = useMemo(() => filterOptionalFields(preview?.schema?.optional_fields, selectedPositionKey === null ? null : selectedPositionKey), [preview?.schema?.optional_fields, selectedPositionKey]);
  const isAnyPosition = selectedPositionKey === null;
  const canProceedStep1 = selectedEmploymentType && startDate && selectedWA && selectedPositionKey !== undefined && selectedPositionKey !== '';
  const isFormValid = useMemo(() => isFormComplete(preview, selectedEmploymentType, startDate, compensationType, formValues), [preview, formValues, compensationType, selectedEmploymentType, startDate]);

  /* --- Local handlers --- */
  const handleFieldChange = (key: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    if (validationErrors[key]) setValidationErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };
  const handleBlockChange = (blockKey: string, value: string) => { setBlockValues((prev) => ({ ...prev, [blockKey]: value })); };

  const applyPositionPrefills = () => {
    if (!selectedPositionKey || selectedPositionKey === '') return;
    const position = positions.find((p) => p.position_key === selectedPositionKey);
    if (!position) return;
    setOriginalFormValues({ ...formValues });
    const nv: Record<string, any> = { ...formValues };
    if (position.position_title_default || position.label) nv['employee.position_title'] = position.position_title_default || position.label;
    if (position.duties_richtext_default) nv['role_description_text'] = position.duties_richtext_default;
    if (position.default_compensation_type) {
      setCompensationType(position.default_compensation_type);
      if (position.default_compensation_type === 'hourly') {
        if (position.default_hourly_rate) { nv['hourly_rate'] = position.default_hourly_rate; setHourlyRate(position.default_hourly_rate); }
        if (position.default_monthly_hours) { nv['monthly_hours'] = position.default_monthly_hours; nv['contract_hours_per_week'] = monthlyToWeekly(position.default_monthly_hours); }
      } else if (position.default_compensation_type === 'salary') {
        if (position.default_salary_monthly_cents) { nv['salary_monthly_cents'] = position.default_salary_monthly_cents; setSalaryMonthlyCents(position.default_salary_monthly_cents); }
        if (position.default_monthly_hours) { nv['monthly_hours'] = position.default_monthly_hours; nv['contract_hours_per_week'] = monthlyToWeekly(position.default_monthly_hours); }
      }
    }
    if (position.default_probation_months != null) { setProbationMonths(position.default_probation_months); nv['probation_months'] = position.default_probation_months; }
    const t = (selectedEmploymentType || '').toUpperCase();
    if (position.default_compensation_type === 'hourly' && t !== 'MINIJOB' && !nv['monthly_hours'] && nv['contract_hours_per_week']) nv['monthly_hours'] = weeklyToMonthly(Number(nv['contract_hours_per_week']));
    setFormValues(nv); setPrefillsApplied(true);
  };

  const resetPrefills = () => {
    setFormValues(originalFormValues); setPrefillsApplied(false);
    setHourlyRate(originalFormValues['hourly_rate'] || null);
    setSalaryMonthlyCents(originalFormValues['salary_monthly_cents'] || null);
    setProbationMonths(originalFormValues['probation_months'] || null);
  };

  const handleNext = async () => { if (activeStep === 0) { await handlers.handlePreview(); setActiveStep(1); } };
  const handleBack = () => { setActiveStep((prev) => prev - 1); };

  /* --- Render --- */
  if (!open) return null;

  return (
    <>
      <div className={asDrawer ? css.overlayDrawer : css.overlayCenter} onClick={onClose}>
        <div className={asDrawer ? css.modalDrawer : css.modalCenter} onClick={(e) => e.stopPropagation()}>
          <div className={css.header}>
            <div className={css.headerLeft}>
              {previewSrc && <button onClick={() => setPreviewSrc(undefined)} className={css.backButton} title="Zurück zum Wizard"><FaArrowLeft size={16} /></button>}
              <h2 className={css.headerTitle}>{previewSrc ? 'Contract Preview' : 'Contract Wizard'}</h2>
            </div>
          </div>

          <div className={previewSrc ? css.contentFlush : css.contentPadded}>
            {previewSrc ? (<PreviewPane src={previewSrc} onRefresh={handlers.handlePreviewPdf} loading={loadingPdfPreview} />) : (
              <div>
                <StepIndicator steps={steps} activeStep={activeStep} />
                {error && (
                  <div className={css.errorPanel}>
                    <div className={css.errorTitle}>{'❌ Failed to preview contract'}</div>
                    <details className={css.errorDetails}><summary className={css.errorDetailsSummary}>{'📋 Show Details'}</summary><pre className={css.errorPre}>{error}</pre></details>
                    <button onClick={handlers.handlePreview} disabled={loadingPreview} className={css.errorRetryBtn}>{loadingPreview ? 'Retrying...' : '🔄 Retry'}</button>
                  </div>
                )}
                <div className={css.stepContent}>
                  {activeStep === 0 && <ContractStepPosition selectedWA={selectedWA} setSelectedWA={setSelectedWA} selectedEmploymentType={selectedEmploymentType} setSelectedEmploymentType={setSelectedEmploymentType} selectedPositionKey={selectedPositionKey} setSelectedPositionKey={setSelectedPositionKey} startDate={startDate} setStartDate={setStartDate} workAreas={workAreas} positions={positions} filteredPositions={filteredPositions} loadingPositions={loadingPositions} isAnyPosition={isAnyPosition} onOpenAnnexModal={() => setAnnexModalOpen(true)} />}
                  {activeStep === 1 && (
                    <div className={css.step1Wrapper}>
                      <ContractStepDetails loadingPreview={loadingPreview} loadingTemplates={loadingTemplates} selectedEmploymentType={selectedEmploymentType} selectedEmploymentTypeLabel={selectedEmploymentTypeLabel} startDate={startDate} selectedWA={selectedWA} selectedWAName={selectedWAName} selectedPositionKey={selectedPositionKey} selectedPositionLabel={selectedPositionLabel} preview={preview} selectedTemplateId={selectedTemplateId} selectedSuggestionIdx={selectedSuggestionIdx} setSelectedSuggestionIdx={setSelectedSuggestionIdx} setSelectedTemplateId={setSelectedTemplateId} setActiveStep={setActiveStep} patchProfile={patchProfile} setPatchProfile={setPatchProfile} prefillsApplied={prefillsApplied} resetPrefills={resetPrefills} handlePreviewPdf={handlers.handlePreviewPdf} loadingPdfPreview={loadingPdfPreview} pdfUrl={pdfUrl} previewSrc={previewSrc} compensationType={compensationType} setCompensationType={setCompensationType} formValues={formValues} handleFieldChange={handleFieldChange} validationErrors={validationErrors} minijobWarning={minijobWarning} filteredRequiredFields={filteredRequiredFields} filteredOptionalFields={filteredOptionalFields} visibleOptionalFieldKeys={visibleOptionalFieldKeys} setVisibleOptionalFieldKeys={setVisibleOptionalFieldKeys} presence={presence} collapsedBlocks={collapsedBlocks} setCollapsedBlocks={setCollapsedBlocks} blockValues={blockValues} handleBlockChange={handleBlockChange} isAdmin={isAdmin} isManager={isManager} attachments={attachments} attachmentSelections={attachmentSelections} setAttachmentSelections={setAttachmentSelections} attachmentValidationErrors={attachmentValidationErrors} makeCoverPage={makeCoverPage} setMakeCoverPage={setMakeCoverPage} makeCombinedPdf={makeCombinedPdf} setMakeCombinedPdf={setMakeCombinedPdf} allTemplates={allTemplates} handleManualTemplateSelect={handlers.handleManualTemplateSelect} handleNavigateToLibrary={handlers.handleNavigateToLibrary} handleSeedTemplates={handlers.handleSeedTemplates} seeding={seeding} onOpenAnnexModal={() => setAnnexModalOpen(true)} hourlyRate={hourlyRate} setHourlyRate={setHourlyRate} salaryMonthlyCents={salaryMonthlyCents} setSalaryMonthlyCents={setSalaryMonthlyCents} contractEndDate={contractEndDate} setContractEndDate={setContractEndDate} probationMonths={probationMonths} setProbationMonths={setProbationMonths} extraLeaveDays={extraLeaveDays} setExtraLeaveDays={setExtraLeaveDays} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!previewSrc && (
            <div className={css.footer}>
              <button onClick={onClose} className={css.buttonSecondary}>Cancel</button>
              <div className={css.footerRight}>
                {activeStep > 0 && <button onClick={handleBack} className={css.buttonSecondary}>Back</button>}
                {activeStep === 0 && <button onClick={handleNext} disabled={!canProceedStep1 || loadingPreview} className={!canProceedStep1 || loadingPreview ? css.buttonDisabled : css.buttonPrimary}>{loadingPreview ? 'Loading...' : 'Next'}</button>}
                {mergeWarnings.length > 0 && (
                  <div className={css.mergeWarningsBox}>
                    <div className={css.mergeWarningsTitle}>{'⚠️ Vertrag erfolgreich erstellt mit Warnungen:'}</div>
                    {mergeWarnings.map((w, i) => <div key={i} className={css.mergeWarningItem}>{'• '}{w}</div>)}
                    <div className={css.mergeWarningsAuto}>Dieser Dialog schließt sich automatisch in wenigen Sekunden...</div>
                  </div>
                )}
                {activeStep === 1 && <button onClick={handlers.handleCreate} disabled={loading || !selectedTemplateId || !isFormValid || mergeWarnings.length > 0} className={loading || !selectedTemplateId || !isFormValid || mergeWarnings.length > 0 ? css.buttonDisabled : css.buttonPrimary}>{loading ? 'Creating...' : mergeWarnings.length > 0 ? 'Erfolgreich erstellt!' : 'Create Contract'}</button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {showPinDialog && (
        <div className={css.pinOverlay} onClick={cancelUnlock}>
          <div className={css.pinDialog} onClick={(e) => e.stopPropagation()}>
            <div className={css.pinDialogHeader}><h3 className={css.pinDialogTitle}>Enter Compensation PIN</h3></div>
            <div className={css.pinDialogBody}>
              <p className={css.pinDialogDesc}>Creating a contract with compensation data requires PIN verification.</p>
              <form onSubmit={async (e) => { e.preventDefault(); await handlePinSubmit(new FormData(e.currentTarget).get('pin') as string); }}>
                <div>
                  <label className={css.labelStyle}>PIN</label>
                  <input type="password" name="pin" autoFocus className={css.inputStyle} style={pinError ? { border: '1px solid #ef4444' } : { border: '1px solid #e5e7eb' }} />
                  {pinError && <div className={css.pinDialogError}>{pinError}</div>}
                </div>
                <div className={css.pinDialogActions}>
                  <button type="button" onClick={cancelUnlock} className={css.buttonSecondary}>Cancel</button>
                  <button type="submit" className={css.buttonPrimary}>Unlock</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {annexModalOpen && (
        <AnnexQuickCreateModal isOpen={annexModalOpen} onClose={() => setAnnexModalOpen(false)} employeeId={employeeId}
          onSuccess={(doc) => { toast.success('Annex-Dokument erfolgreich erstellt'); setAnnexModalOpen(false); if (onSuccess && doc) onSuccess({ document_id: String(doc.id), pdf_url: doc.pdf_url || '', trace_id: doc.error_trace_id || '' }); }} />
      )}
    </>
  );
}

function StepIndicator({ steps, activeStep }: { steps: string[]; activeStep: number }) {
  return (
    <div className={css.stepperWrapper}>
      <div className={css.stepperTrack}>
        {steps.map((label, idx) => {
          const isDone = idx < activeStep, isCurrent = idx === activeStep;
          return (
            <div key={label} className={css.stepItem}>
              {idx > 0 && <div className={isDone ? css.stepConnectorDone : css.stepConnectorPending} />}
              <div className={isDone || isCurrent ? css.stepCircleActive : css.stepCirclePending}>
                {isDone ? <FaCheck size={14} color="#fff" /> : isCurrent ? <FaCircle size={8} color="#fff" /> : <FaCircle size={8} color="#e5e7eb" />}
              </div>
              <div className={isDone || isCurrent ? (isCurrent ? css.stepLabelCurrent : css.stepLabelDone) : css.stepLabelPending}>{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ContractWizardModal;
